import React, { useEffect } from "react";
import {
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  Popconfirm,
  FormInstance,
  InputNumber,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";

interface trackRecordItem {
  title: string;
  reference: number;
  symbol: string;
}

interface WhyChooseTQPProps {
  heading?: string;
  subtext?: string[];
  trackRecord?: trackRecordItem[];
}

interface Props {
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  onDelete: (index: number, field: "Why Choose TQP") => void;
  form: FormInstance;
  whyChooseTQPData?: WhyChooseTQPProps;
  loading?: boolean;
  mode: "list" | "add" | "edit";
  setMode: (value: "list" | "add" | "edit") => void;
  editingIndex: number | null;
  setEditingIndex: (value: number | null) => void;
}

const WhyChooseTQP: React.FC<Props> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  whyChooseTQPData,
  onDelete,
  loading,
  mode,
  setMode,
  editingIndex,
  setEditingIndex,
}) => {
  useEffect(() => {
    if (!whyChooseTQPData) return;

    if (mode === "list") {
      form.setFieldsValue({
        why_choose_tqp: {
          heading: whyChooseTQPData.heading ?? "",
          subtext: whyChooseTQPData.subtext ?? [""],
          trackRecord: whyChooseTQPData.trackRecord ?? [],
        },
      });
    }

    if (mode === "add") {
      form.setFieldsValue({
        why_choose_tqp: {
          trackRecord: [{ title: "", reference: "", symbol: "" }],
        },
      });
    }

    if (mode === "edit" && editingIndex !== null) {
      const item = whyChooseTQPData.trackRecord?.[editingIndex];
      if (item) {
        form.setFieldsValue({
          why_choose_tqp: {
            trackRecord: [item],
          },
        });
      }
    }
  }, [mode, editingIndex, whyChooseTQPData, form]);

  const trackRecord = whyChooseTQPData?.trackRecord ?? [];

  return (
    <div>
      {isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl mb-4">
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Heading"
              name={["why_choose_tqp", "heading"]}
              rules={[{ required: true, message: "Enter heading" }]}
            >
              <Input placeholder="Enter heading..." />
            </Form.Item>

            <Form.List name={["why_choose_tqp", "subtext"]}>
              {(fields, { add, remove }) => {
                if (fields.length === 0) add();
                return (
                  <>
                    <div className="flex justify-between items-center mb-2">
                      <div>Subtext</div>
                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => add()}
                      >
                        Add Subtext
                      </Button>
                    </div>

                    {fields.map((field) => (
                      <div key={field.key} className="p-2 rounded mb-4">
                        <Form.Item
                          name={field.name}
                          rules={[
                            {
                              required: true,
                              message: "Enter subtext",
                            },
                          ]}
                        >
                          <TextArea rows={3} placeholder="Enter subtext..." />
                        </Form.Item>

                        {fields.length > 1 && (
                          <div className="flex justify-end">
                            <MinusOutlined
                              className="text-red-500 border border-red-500 rounded-full cursor-pointer p-1"
                              onClick={() => remove(field.name)}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                );
              }}
            </Form.List>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                setIsEditing(false);
                handleCancel();
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}

      {mode === "list" && !isEditing && (
        <div className="rounded-[20px] overflow-hidden">
          <div className="hidden sm:block">
            <table className="min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Heading</th>
                  <th className="py-3 px-4 text-left">Subtext</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>

              <tbody className="bg-[#F9F9F9]">
                {whyChooseTQPData?.heading ? (
                  <tr className="border-b">
                    <td className="px-4 py-3">{whyChooseTQPData?.heading}</td>
                    <td className="px-4 py-3">
                      <p className="line-clamp-2">
                        {whyChooseTQPData?.subtext?.[0]}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <EditOutlined
                        className="text-primary text-xl cursor-pointer"
                        onClick={() => setIsEditing(true)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-6">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Data
                      </Button>
                    </td>
                  </tr>
                )}

                <tr>
                  {
                    trackRecord?.length < 4 && <td colSpan={4} className="px-4 py-3 text-right">
                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setMode("add");
                          setEditingIndex(null);
                        }}
                      >
                        Add Track Record
                      </Button>
                    </td>
                  }

                </tr>

                <tr>
                  <td colSpan={4} className="p-4">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-4 py-2 text-left">Title</th>
                          <th className="px-4 py-2 text-left">Reference</th>
                          <th className="px-4 py-2 text-right">Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {trackRecord?.length ? (
                          trackRecord?.map((item, index) => (
                            <tr key={index} className="border-t">
                              <td className="px-4 py-2">{item?.title}</td>
                              <td className="px-4 py-2">
                                {item?.reference}
                                {item?.symbol}
                              </td>
                              <td className="px-4 py-2 text-right">
                                <div className="flex justify-end gap-4">
                                  <EditOutlined
                                    className="cursor-pointer text-primary"
                                    onClick={() => {
                                      setMode("edit");
                                      setEditingIndex(index);
                                    }}
                                  />
                                  <Popconfirm
                                    title="Delete this trackRecord?"
                                    onConfirm={() =>
                                      onDelete(index, "Why Choose TQP")
                                    }
                                  >
                                    <DeleteOutlined className="cursor-pointer text-red-500" />
                                  </Popconfirm>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={5}
                              className="text-center py-6 text-gray-500"
                            >
                              No Record added
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="sm:hidden space-y-4">
            {whyChooseTQPData?.heading ? (
              <div className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm">
                <div>
                  <span className="font-semibold text-primary">Heading: </span>
                  <p className="">
                    {whyChooseTQPData.heading}
                  </p>
                </div>
                <div>
                  <span className="font-semibold text-primary">Subtext: </span>
                  <div className="line-clamp-2">
                    {whyChooseTQPData?.subtext?.[0]}
                  </div>
                </div>

                <div>
                  <span className="font-semibold text-primary block my-1">
                    Action:
                  </span>
                  <EditOutlined
                    onClick={() => setIsEditing(true)}
                    className="cursor-pointer text-black text-[18px]"
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <Button type="dashed" onClick={() => setIsEditing(true)}>
                  Add Data
                </Button>
              </div>
            )}

            {trackRecord.length ? (
              trackRecord.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm"
                >
                  <p>
                    <span className="font-semibold text-primary">Title: </span>{" "}
                    {item?.title}
                  </p>
                  <p>
                    <span className="font-semibold text-primary">
                      Reference:{" "}
                    </span>{" "}
                    {item?.reference}
                    {item?.symbol}
                  </p>
                  <div className="flex justify-end gap-3 mt-2">
                    <EditOutlined
                      className="cursor-pointer text-primary"
                      onClick={() => {
                        setMode("edit");
                        setEditingIndex(index);
                      }}
                    />
                    <Popconfirm
                      title="Delete this highlight?"
                      onConfirm={() => onDelete(index, "Why Choose TQP")}
                    >
                      <DeleteOutlined className="cursor-pointer text-red-500" />
                    </Popconfirm>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-6">
                No Record added
              </div>
            )}
            {(trackRecord?.length < 4 &&
              <div className="text-right">
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setMode("add");
                    setEditingIndex(null);
                  }}
                >
                  Add Track Record
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {mode !== "list" && !isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl">
          <Form layout="vertical" form={form}>
            <Form.List name={["why_choose_tqp", "trackRecord"]}>
              {(fields) => (
                <>
                  {fields.map((field) => (
                    <div key={field.key} className="border p-4 rounded mb-4">
                      <Form.Item
                        label="Title"
                        name={[field.name, "title"]}
                        rules={[{ required: true, message: "Enter title..." }]}
                      >
                        <Input placeholder="Enter title..." />
                      </Form.Item>

                      <div className="grid grid-cols-2 gap-5">
                        <Form.Item
                          label="Reference Number"
                          name={[field.name, "reference"]}
                          rules={[
                            { required: true, message: "Enter reference..." },
                          ]}
                        >
                          <InputNumber
                            className="w-full"
                            placeholder="Enter reference..."
                          />
                        </Form.Item>

                        <Form.Item label="Symbol" name={[field.name, "symbol"]}>
                          <Input placeholder="Enter symbol..." />
                        </Form.Item>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                handleCancel();
                setMode("list");
                setEditingIndex(null);
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhyChooseTQP;
